import { useState, useEffect } from 'react';
import { CalendarEvent } from '../../../mocks/calendarEvents';

interface FeaturedEventsBarProps {
  events: CalendarEvent[];
}

const categoryBadgeColors: Record<string, string> = {
  Concert: 'bg-amber-100 text-amber-700',
  Festival: 'bg-rose-100 text-rose-700',
  Market: 'bg-emerald-100 text-emerald-700',
  Family: 'bg-sky-100 text-sky-700',
  Sports: 'bg-orange-100 text-orange-700',
  'Arts & Culture': 'bg-violet-100 text-violet-700',
  Community: 'bg-cyan-100 text-cyan-700',
  Holiday: 'bg-red-100 text-red-700',
};

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
}

export default function FeaturedEventsBar({ events }: FeaturedEventsBarProps) {
  const featuredEvents = events.filter((e) => e.featured).slice(0, 4);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (featuredEvents.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % featuredEvents.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [featuredEvents.length]);

  if (featuredEvents.length === 0) return null;

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + featuredEvents.length) % featuredEvents.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % featuredEvents.length);
  };

  return (
    <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-star-fill text-xl"></i>
            </div>
            
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold mb-1 opacity-90">FEATURED EVENT</div>
              <div className="flex items-center gap-4 overflow-hidden">
                <a 
                  href={`/events/${featuredEvents[currentIndex].id}`}
                  className="font-bold hover:underline truncate"
                >
                  {featuredEvents[currentIndex].title}
                </a>
                <span className="text-sm opacity-90 whitespace-nowrap hidden sm:inline">
                  {featuredEvents[currentIndex].date} • {featuredEvents[currentIndex].location}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <a
              href="/promote-event"
              className="hidden md:flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap"
            >
              <i className="ri-megaphone-line"></i>
              <span>Promote Event</span>
            </a>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrev}
                className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                aria-label="Previous event"
              >
                <i className="ri-arrow-left-s-line text-xl"></i>
              </button>
              <button
                onClick={handleNext}
                className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                aria-label="Next event"
              >
                <i className="ri-arrow-right-s-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
